import { t } from 'i18next';
import isElectron from 'is-electron';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import CloseWhite from '../../assets/icons/cross--white.svg';
import YellowInfoCircle from '../../assets/icons/exclamation-mark-circle--yellow.svg';
import Loader from '../../components/Loader/Loader';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { palette } from '../../theme/colors';
import { useConfirmation } from '../../utils/ConfirmationServiceContext/confirmationContext';
import { convertSecondsToTime } from '../../utils/date';
import {
  SDeactivateButton,
  SNavigateToChatButton,
  SNotifyInfoRow,
  SPage,
  STimeAddButton,
  STimeAddRow,
  STimeAddedBanner,
  STimerActionsContainer,
  STimerCircle,
  STimerContainer,
  STimerTitleText,
} from './LoneWorkerTimer.styles';
import { calcLwtProgress, secondsToTimespan, timespanToSeconds } from './helpers';
import { getLwtAlarmData, selectIsLwtLoading } from './loneWorkerTimerSlice';
import {
  deactivateAlarm,
  fetchAlarmData,
  fetchFinishedAlarmData,
  goToAlarmChat,
  LwtDispatchTimeExceeds24h,
  setOffTimer,
  updateTimer,
} from './loneWorkerTimerSlice/actionCreator';
import { localStorageGet, localStorageRemove, localStorageSet } from '../../utils/storage';
import { useFeatureFlag } from '../../featureFlags/hooks';
import { LoneWorkerTimerDispatched } from './LoneWorkerTimerDispatched';

const dayInSeconds = 86399 as const;

export const LoneWorkerTimer = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const confirm = useConfirmation();
  const isLoading = useSelector(selectIsLwtLoading);
  const alarmData = useAppSelector(getLwtAlarmData);
  const holdOffset = 3000;
  const isPulsatingScreenEnabled = useFeatureFlag('lwt-sender-screen-2026-web');
  const [addedBannerText, setAddedBannerText] = useState<string | null>(null);
  const [millisecondsHold, setMillisecondsHold] = useState(0);
  const [isHolding, setIsHolding] = useState(false);
  const [seconds, setSeconds] = useState(() =>
    alarmData ? Math.round(Math.max(0, timespanToSeconds(alarmData.DispatchDelay))) : dayInSeconds
  );
  const [isLWTDeactivated, setIsLWTDeactivated] = useState(false);
  const holdProgress = useMemo(() => millisecondsHold / holdOffset, [millisecondsHold]);
  const progress = useMemo(() => {
    if (!alarmData) return 0;
    return calcLwtProgress(alarmData, seconds);
  }, [seconds, alarmData]);
  const isTimerCounting = !!alarmData?.Id && seconds > 0;

  function applySeconds() {
    if (!alarmData) return;

    const secondsLeft = Math.round(Math.max(0, timespanToSeconds(alarmData.DispatchDelay)));
    setSeconds(secondsLeft);
  }

  const listenForReturnToForeground = () => {
    if (isElectron() && localStorageGet('isLoneWorkTimerActive')) {
      dispatch(fetchFinishedAlarmData(navigate));
    }
  };

  const onErrorFeedback = (errorTx: string) => {
    confirm({
      title: 'warning',
      description: errorTx,
      onSubmit: () => {},
      confirmText: 'ok',
    });
  };

  useEffect(() => {
    const subscription = window.electronApi?.onAppForeground(() => listenForReturnToForeground());
    return () => subscription?.remove();
  }, []);

  useEffect(() => {
    if (!isTimerCounting) return;

    const interval = setInterval(() => {
      setSeconds(prev => Math.max(Math.round(prev) - 1, 0));
    }, 1000);
    return () => clearInterval(interval);
  }, [isTimerCounting]);

  useEffect(() => {
    dispatch(fetchAlarmData(navigate, false));
  }, []);

  useEffect(() => {
    //used if the application will be launched in a browser.
    //on browser nav back click redirect to the dashboard
    if (!isElectron()) {
      window.addEventListener('popstate', () => {
        navigate('/dashboard');
      });
    }

    if (!alarmData && !isLWTDeactivated) {
      dispatch(fetchAlarmData(navigate, false));
    } else {
      localStorageSet('isLoneWorkTimerActive', JSON.stringify(true));
      applySeconds();
    }
  }, [alarmData, isLWTDeactivated]);

  useEffect(() => {
    let interval: NodeJS.Timeout | undefined = undefined;
    if (isHolding) {
      interval = setInterval(() => {
        setMillisecondsHold(val => {
          const newValue = val + 100;
          if (newValue >= holdOffset) {
            clearInterval(interval);
          }
          return newValue > 0 ? newValue : 0;
        });
      }, 100);
    } else if (!isHolding && millisecondsHold > 0) {
      clearInterval(interval);
      interval = setInterval(() => {
        setMillisecondsHold(val => {
          const newValue = val - 100;
          if (newValue <= 0) {
            clearInterval(interval);
          }
          return newValue > 0 ? newValue : 0;
        });
      }, 100);
    }
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [isHolding]);

  useEffect(() => {
    if (millisecondsHold === holdOffset) {
      dispatch(setOffTimer(navigate, onErrorFeedback));
    }
  }, [millisecondsHold]);

  useEffect(() => {
    if (addedBannerText) {
      setTimeout(() => {
        setAddedBannerText(null);
      }, 3000);
    }
  }, [addedBannerText]);

  const addTime = async (secondsToAdd: number) => {
    if (!alarmData) return;

    const updateTimerData = {
      deviceID: alarmData.DeviceId,
      dispatchProlong: secondsToTimespan(secondsToAdd),
      locationId: alarmData.LocationId,
    };
    try {
      await dispatch(updateTimer(updateTimerData));
      const minutesAdded = Math.floor(secondsToAdd / 60);
      setAddedBannerText(
        `${t('alarm_added')} ${minutesAdded} ${t('alarm_minutes').toLocaleLowerCase()}`
      );
    } catch (error) {
      if (error instanceof LwtDispatchTimeExceeds24h) {
        confirm({
          title: 'warning',
          description: 'alarm_timer_over_24_hours_warning_description',
          onSubmit: () => {},
          confirmText: 'ok',
        });
      }
    }
  };

  const cancelAlarm = async () => {
    setIsLWTDeactivated(true);
    localStorageRemove('isLoneWorkTimerActive');
    dispatch(deactivateAlarm(navigate, '/dashboard'));
  };

  const onDeactivateClick = () => {
    confirm({
      title: 'confirmation_required',
      description: 'alarm_timer__cancel_confirmation__description',
      onSubmit: () => cancelAlarm(),
      onCancel: () => {},
      confirmText: 'alarm_deactivate',
      cancelText: 'cancel',
    });
  };

  const goToChat = () => {
    localStorageRemove('isLoneWorkTimerActive');
    dispatch(goToAlarmChat(navigate, onErrorFeedback));
  };

  const changeHoldingState = (newState: boolean) => {
    if (seconds > 0) {
      setIsHolding(newState);
    }
  };

  if (alarmData && seconds <= 0 && !isLWTDeactivated && isPulsatingScreenEnabled) {
    return <LoneWorkerTimerDispatched onOpenMessagePress={goToChat} isLoading={isLoading} />;
  }

  if (isLoading) {
    return (
      <SPage>
        <Loader />
      </SPage>
    );
  }

  return (
    <SPage>
      {addedBannerText && (
        <STimeAddedBanner role="timeAddedBanner">
          <p role="text">{addedBannerText}</p>
          <img onClick={() => setAddedBannerText(null)} src={CloseWhite} role="closeIcon" />
        </STimeAddedBanner>
      )}
      <STimerContainer role="timerContainer">
        <STimerTitleText role="timerTitleText">
          {seconds > 0 &&
            t<string>(isHolding ? 'alarm_timer_hold_desc_active' : 'alarm_timer_hold_desc')}
        </STimerTitleText>
        <STimerCircle
          onMouseDown={() => changeHoldingState(true)}
          onMouseUp={() => changeHoldingState(false)}
          onMouseLeave={() => changeHoldingState(false)}
          role="timerCircle"
        >
          {seconds > 0 && (
            <svg width="280" height="280" viewBox="0 0 280 280">
              <circle
                r="136"
                cx="140"
                cy="140"
                fill={palette.transparent}
                stroke={seconds < 60 ? palette.danger : palette.earthYellow}
                strokeLinecap="round"
                strokeWidth="6px"
                strokeDasharray={`${String(3.14 * 136 * 2)}px`}
                strokeDashoffset={`${String(3.14 * 136 * 2 * progress)}px`}
              ></circle>
              <circle
                r="134"
                cx="140"
                cy="140"
                fill={palette.transparent}
                stroke={millisecondsHold ? palette.danger : palette.transparent}
                strokeWidth="12px"
                strokeDasharray={`${String(3.14 * 134 * 2)}px`}
                strokeDashoffset={`${Number(String(3.14 * 134 * 2)) * (1 + holdProgress)}px`}
              ></circle>
            </svg>
          )}
          <h1>{convertSecondsToTime(seconds)}</h1>
        </STimerCircle>
        {seconds > 0 ? (
          <>
            <SNotifyInfoRow role="notifyInfoRow">
              {alarmData?.oneMinuteWarning && (
                <>
                  <img src={YellowInfoCircle} role="icon" />
                  <p role="reminderText">{t('notification_reminder')}</p>
                </>
              )}
            </SNotifyInfoRow>
          </>
        ) : (
          <SNavigateToChatButton onClick={goToChat} tx="alarm_open_alarm_message" />
        )}
      </STimerContainer>
      {seconds > 0 && (
        <STimerActionsContainer isTransparent={isHolding} role="timerActionsContainer">
          <div className="content" role="timeAddRowContainer">
            <STimeAddRow role="timeAddRow">
              <STimeAddButton
                text={t('work_alone_alarm_add10min')}
                onClick={() => addTime(600)}
                role="add10MinutesButton"
              ></STimeAddButton>
              <STimeAddButton
                text={t('work_alone_alarm_add30min')}
                onClick={() => addTime(1800)}
                role="add30MinutesButton"
              ></STimeAddButton>
            </STimeAddRow>
            <SDeactivateButton
              color="red"
              text={t('alarm_recall')}
              onClick={onDeactivateClick}
              role="deactivateBtn"
            ></SDeactivateButton>
          </div>
        </STimerActionsContainer>
      )}
    </SPage>
  );
};
